import React, { useState, useEffect, useContext } from "react";
import LmsProfileForm from "./LmsProfileForm";
import LmsCards from "./LmsCards";
import { eachDayOfInterval, format, parseISO } from "date-fns";
import ApiCall from "../utils/ApiCall";
import AuthContext from "../contexts/AuthContext";
import { APPLYLEAVE, HOLIDAYCALENDARLEAVES } from "../Constants";
import { useStateContext } from "../contexts/ContextProvider";

const LeaveRequest = () => {
	const { cardData } = useStateContext();
	const { authTokens } = useContext(AuthContext);
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [selectedOptionLeave, setSelectedOptionLeave] = useState("sick");
	const [selectedOptionType, setSelectedOptionType] = useState("half");
	const [selectedOptinalLeaveType, setSelectedOptinalLeaveType] =
		useState("2023-06-29");
	const [description, setDescription] = useState("");
	const [totalLeave, setTotalLeave] = useState("");
	const [optionalHoliday, setOptionalHoliday] = useState([]);
	const [holidayLeaves, setHolidayLeaves] = useState([]);

	// modal state
	const [showModal, setShowModal] = useState(false);
	const [modalMessage, setModalMessage] = useState("");

	const optionsOne = [
		{ label: "Sick Leave", value: "sick" },
		{ label: "Casual Leave", value: "casual" },
		{ label: "Optional Leave", value: "optional" },
		{ label: "Privilege Leave", value: "granted" },
	];
	const optionsTwo = [
		{ label: "Half Day", value: "half" },
		{ label: "Full Day", value: "full" },
	];

	useEffect(() => {
		fetchOptionalFilterData();
		fetchHolidayLeave();
	}, []);

	const fetchOptionalFilterData = async () => {
		try {
			const res = await ApiCall(APPLYLEAVE, "GET", authTokens, null);
			setOptionalHoliday(res.data.holiday);
		} catch (error) {
			console.log("Error , while fetching filterOptionData", error);
		}
	};

	const fetchHolidayLeave = async () => {
		try {
			const res = await ApiCall(
				HOLIDAYCALENDARLEAVES,
				"POST",
				authTokens,
				null
			);
			setHolidayLeaves(res.data.holiday);
		} catch (error) {
			console.log("Error , while fetching Holiday Leaves", error);
		}
	};

	const handleLeave = (event) => {
		event.preventDefault();
		setSelectedOptionLeave(event.target.value);
	};

	const handleType = (event) => {
		event.preventDefault();
		setSelectedOptionType(event.target.value);
	};

	const handleOptionalLeave = (event) => {
		event.preventDefault();
		setSelectedOptinalLeaveType(event.target.value);
	};

	const handleStartDate = (event) => {
		event.preventDefault();
		setStartDate(event.target.value);
	};

	const handleEndDate = (event) => {
		event.preventDefault();
		const selectedDate = event.target.value;
		setEndDate(selectedDate);
	};

	useEffect(() => {
		if (startDate && endDate) {
			const calDuration = leaveDuration(endDate);
			setEndDate(calDuration);
		}
	}, [startDate, endDate, selectedOptionType]);

	const leaveDuration = (selectedDate) => {
		const start = parseISO(startDate);
		const end = parseISO(selectedDate);

		if (start > end || end < start) {
			setTotalLeave(0);
			return format(end, "yyyy-MM-dd");
		}

		const dates = eachDayOfInterval({
			start: start,
			end: end,
		});

		const leaveDays = dates
			.map((date) => format(date, "yyyy-MM-dd"))
			.filter((date) => {
				const day = new Date(date).getDay();
				return day !== 6 && day !== 0;
			});

		const filteredDates = leaveDays.filter(
			(date) => !holidayLeaves.includes(date)
		);

		const totalLeaveDays = filteredDates.length;
		let calculatedTotalLeave = totalLeaveDays;

		if (selectedOptionType === "half") {
			calculatedTotalLeave = totalLeaveDays / 2;
		}

		setTotalLeave(calculatedTotalLeave);

		return format(end, "yyyy-MM-dd");
	};

	const handleAreaValue = (event) => {
		event.preventDefault();
		setDescription(event.target.value);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		const params = {
			selectedOptionLeave,
			selectedOptionType,
			startDate,
			endDate,
			totalLeave,
			description,
		};

		try {
			const response = await ApiCall(APPLYLEAVE, "POST", authTokens, params);
			if (response.status === 200) {
				setModalMessage("Leave request submitted successfully!");
				setShowModal(true);
			} else {
				setModalMessage("Sorry!, Something went wrong");
				setShowModal(true);
			}
			// reset
			setStartDate("");
			setEndDate("");
			setTotalLeave("");
			setDescription("");
			setSelectedOptionLeave("sick"); // reset leave type
			setSelectedOptionType("half");
		} catch (error) {
			console.error("Error fetching data", error);
		}
	};

	const handleOptionalSubmit = async (event) => {
		event.preventDefault();
		const params = {
			selectedOptionLeave,
			selectedOptinalLeaveType,
		};
		try {
			const response = await ApiCall(APPLYLEAVE, "POST", authTokens, params);
			if (response.status === 200) {
				setModalMessage("Optional/Privilege Leave applied successfully!");
				setShowModal(true);
			} else {
				setModalMessage("Sorry!, Something went wrong");
				setShowModal(true);
			}
			// reset dropdowns
			setSelectedOptionLeave("sick");
			setSelectedOptinalLeaveType("");
		} catch (error) {
			console.error("Error fetching data", error);
		}
	};

	return (
		<div className="w-auto my-6 mx-6 py-4 px-2">
			{/* cards */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 pb-6">
				{cardData &&
					cardData.map((item, index) => (
						<LmsCards
							key={index}
							number={item.number}
							title={item.title}
							icons={item.icons}
							color={item.color}
						/>
					))}
			</div>

			{/* leave form */}
			<div className="box-shadow intro-y bg-accent transition flex flex-col items-center justify-center rounded-3xl w-auto pb-4">
				<div className="w-full intro-y bg-blue-900 flex rounded-t-3xl justify-center items-center py-3 mb-4">
					<h4 className="mx-6 text-center table-header-text font-bold text-lg">
						Leave Request
					</h4>
				</div>
				<LmsProfileForm
					selectedOptionLeave={selectedOptionLeave}
					setSelectedOptionLeave={setSelectedOptionLeave}
					selectedOptionType={selectedOptionType}
					setSelectedOptionType={setSelectedOptionType}
					startDate={startDate}
					endDate={endDate}
					handleLeave={handleLeave}
					handleSubmit={handleSubmit}
					handleStartDate={handleStartDate}
					handleEndDate={handleEndDate}
					optionsOne={optionsOne}
					optionsTwo={optionsTwo}
					handleAreaValue={handleAreaValue}
					description={description}
					totalLeave={totalLeave}
					handleType={handleType}
					optionalLeave={optionalHoliday}
					handleOptionalSubmit={handleOptionalSubmit}
					handleOptionalLeave={handleOptionalLeave}
				/>
			</div>

			{/* custom modal */}
			{showModal && (
				<div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
					<div className="bg-white rounded-2xl p-6 w-96 shadow-lg">
						<h3 className="text-lg font-semibold mb-4 text-center">{modalMessage}</h3>
						<div className="flex justify-center">
							<button
								onClick={() => setShowModal(false)}
								className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
							>
								OK
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default LeaveRequest;
